import type en from "../en/studio";
import type { Translation } from "../../types";

const studio: Translation<typeof en> = {
  home: {
    loadError: "Impossible de charger Studio",
    intro:
      "Coupez, réorganisez et combinez des enregistrements, et rédigez vos chapitres à la main. Vos brouillons restent privés jusqu’à leur publication.",
    newProject: "Nouveau projet",
    emptyTitle: "Aucun brouillon pour l’instant",
    emptyDescription:
      "Commencez de zéro ici, ou ouvrez un enregistrement et cliquez sur « Monter dans Studio ».",
    clipCount_one: "{{count}} extrait",
    clipCount_other: "{{count}} extraits",
    edited: "Modifié le {{date}}",
    lastPublish: "Dernière publication",
    deleteProject: "Supprimer {{title}}",
  },
  editor: {
    splitNothing: "La tête de lecture est déjà sur une coupe — rien à scinder.",
    publishedChapters: "Chapitres publiés sur l’enregistrement.",
    publishedRender: "Publication en cours — le rendu s’effectue en arrière-plan.",
    openErrorTitle: "Impossible d’ouvrir ce projet",
    openErrorBody:
      "Il a peut-être été supprimé, ou il appartient à quelqu’un d’autre — les brouillons sont privés à leur auteur.",
    untitled: "Montage sans titre",
    rename: "Renommer ce projet",
    unsavedTitle: "Modifications non enregistrées",
    unsaved: "● non enregistré",
    fitScreenOff: "Laisser l’éditeur dépasser la hauteur de l’écran",
    fitScreenOn: "Adapter tout l’éditeur à l’écran, sans défilement",
    undo: "Annuler (Ctrl+Z)",
    redo: "Rétablir (Ctrl+Maj+Z)",
    saveDraft: "Enregistrer le brouillon (Ctrl+S)",
    publish: "Publier",
    split: "Scinder",
    splitTitle: "Scinder l’extrait sous la tête de lecture (S)",
    duplicate: "Dupliquer",
    duplicateTitle: "Dupliquer l’extrait sélectionné (D)",
    mute: "Couper le son",
    unmute: "Rétablir le son",
    muteTitle: "Couper le son de l’extrait sélectionné (M)",
    unmuteTitle: "Rétablir le son de l’extrait sélectionné (M)",
    removeTitle: "Retirer l’extrait sélectionné (Suppr)",
    hintTransitions:
      "L’aperçu des transitions est approximatif — le rendu publié est exact.",
    hintKeys:
      "Glissez les extraits pour les réordonner · glissez les bords pour couper · S scinde · M coupe le son",
    tabs: {
      sources: "Sources",
      chapters: "Chapitres",
    },
    browseLibrary: "Parcourir la bibliothèque",
    browseLibraryTitle: "Parcourir toute la bibliothèque, avec miniatures et dossiers",
    uploadsNote:
      "Les fichiers importés arrivent dans la bibliothèque — parcourez-la pour les ajouter ici.",
  },
  chapters: {
    defaultTitle: "Chapitre {{number}}",
    none: "Aucun chapitre pour l’instant.",
    count_one: "{{count}} chapitre",
    count_other: "{{count}} chapitres",
    addAt: "Ajouter à {{time}}",
    emptyHelp:
      "Lancez la vidéo et ajoutez un chapitre chaque fois que le sujet change. Le premier chapitre commence au début ; chacun dure jusqu’au suivant — il n’y a rien d’autre à aligner.",
    removeFirst: "Supprimer ce chapitre",
    removeMerge: "Supprimer — fusionne avec le chapitre précédent",
    titlePlaceholder: "Titre du chapitre",
    summaryPlaceholder: "Résumé en une phrase (facultatif)",
  },
  publish: {
    titleChapters: "Publier les chapitres",
    titleEdit: "Publier le montage",
    chaptersOnlyBody:
      "La vidéo elle-même n’a pas changé, donc aucun rendu n’est effectué. Vos chapitres remplacent les chapitres actuels de l’enregistrement source pour tout l’espace de travail — la version précédente reste disponible dans l’historique des versions.",
    renderBody:
      "Cette action génère un <strong>nouvel enregistrement</strong> à partir de votre ligne de temps. Les originaux restent intacts, et le rendu s’effectue en arrière-plan — une longue vidéo prend quelques minutes.",
    titleLabel: "Titre",
    transcriptLegend: "Transcription",
    modes: {
      map: {
        label: "Reporter la transcription à travers le montage",
        detail:
          "Exact et gratuit. Les mots conservent leur minutage, les coupes retirent leurs mots, les noms des intervenants sont conservés. Le bon choix, sauf si les sources n’avaient pas de transcription.",
      },
      asr: {
        label: "Transcrire entièrement la vidéo publiée",
        detail:
          "Lance la reconnaissance vocale sur le résultat. Consomme du crédit de transcription et perd les noms d’intervenants saisis à la main — à utiliser quand les sources étaient des extraits non transcrits.",
      },
      none: {
        label: "Pas de transcription",
        detail:
          "Publier sans transcription. Elle pourra être lancée plus tard depuis la page de l’enregistrement.",
      },
    },
  },
  elements: {
    empty:
      "Rien sur la ligne de temps pour l’instant. Parcourez la bibliothèque pour ajouter la première vidéo.",
    reorderPart: "Réordonner la partie {{number}}",
    dragToReorder: "Glisser pour réordonner",
    fallbackTitle: "Vidéo",
    namePart: "Nommer cette partie",
    muted: "Son coupé",
    hasTransition: "Commence par une transition",
    from: "de",
    to: "à",
    ofDuration: "sur {{duration}}",
  },
  preview: {
    title: "Aperçu",
    empty: "Ajoutez un enregistrement à la ligne de temps pour le prévisualiser.",
  },
  transitions: {
    crossfade: "Fondu enchaîné",
    fadeblack: "Fondu au noir",
    wipeleft: "Volet vers la gauche",
  },
  transitionsShort: {
    crossfade: "FE",
    fadeblack: "FN",
    wipeleft: "VG",
  },
  timeline: {
    fit: "Ajuster",
    fitTitle: "Afficher toute la ligne de temps",
    zoomOut: "Zoom arrière (Ctrl + molette)",
    zoomIn: "Zoom avant (Ctrl + molette)",
    clipFallback: "Extrait",
    muteClip: "Couper le son de cet extrait (M)",
    unmuteClip: "Rétablir le son de cet extrait (M)",
    seam: "{{transition}} · {{seconds}} s",
    seamCapped: "{{transition}} · {{seconds}} s (limité ici — {{requested}} s demandées)",
    addTransition: "Ajouter une transition à cette coupe",
    transitionInto: "Transition vers <name>{{name}}</name> :",
    thisClip: "cet extrait",
    cut: "Coupe",
    for: "durée",
    seconds: "{{seconds}} s",
    capped: "Limité par l’extrait le plus court — dure {{seconds}} s ici.",
    capNote: "Les fondus longs sont limités à la moitié de l’extrait le plus court.",
  },
};

export default studio;
